import type { JSONContent } from "@tiptap/core";
import { Fragment, type ReactNode } from "react";

import { renderTextWithMarks } from "./renderMarks";
import { StaticEntity } from "./StaticEntity";
import { StaticMention } from "./StaticMention";
import type { SgidObjectMap } from "./types";

const renderChildren = (
  node: JSONContent,
  sgidMap: SgidObjectMap,
): ReactNode[] =>
  (node.content ?? []).map((child, index) => renderNode(child, index, sgidMap));

export const renderNode = (
  node: JSONContent,
  key: number,
  sgidMap: SgidObjectMap,
  trailing?: ReactNode,
): ReactNode => {
  switch (node.type) {
    case "paragraph": {
      const children = renderChildren(node, sgidMap);
      return (
        <p key={key}>
          {children.length > 0 ? children : <br />}
          {trailing}
        </p>
      );
    }
    case "text":
      return <Fragment key={key}>{renderTextWithMarks(node)}</Fragment>;
    case "hardBreak":
      return <br key={key} />;
    case "blockquote":
      return <blockquote key={key}>{renderChildren(node, sgidMap)}</blockquote>;
    case "bulletList":
      return <ul key={key}>{renderChildren(node, sgidMap)}</ul>;
    case "orderedList":
      return <ol key={key}>{renderChildren(node, sgidMap)}</ol>;
    case "listItem":
      return <li key={key}>{renderChildren(node, sgidMap)}</li>;
    case "mention": {
      const mention = sgidMap[node.attrs?.sgid];
      return mention ? <StaticMention key={key} mention={mention} /> : null;
    }
    case "entity":
      return <StaticEntity key={key} entity={sgidMap[node.attrs?.sgid]} />;
    default:
      return null;
  }
};
